import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { listVendors, getVendor, createVendor, updateVendor, setRateStatus } from "../../services/catalogueService";
import { listLanes, saveLane, deleteLane, vendorStats } from "../../services/sourcingService";
import { errorToast, successToast } from "../Toasts/Toasts";

// CMS: vendors from Zoho CRM - who quotes what, how recently - and a
// vendor page with their whole rate history by variant. Phone is what the
// WhatsApp loop matches a reply on, so it is the field that matters most.

const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const FIELDS = [["name", "Name"], ["phone", "Phone (WhatsApp)"], ["email", "Email"], ["city", "City"], ["state", "State"]];

// Transport lanes (MG, 22 Sep 2026): a vendor's City is a lane with a minimum
// trip cost; a row pinned to one vendor overrides the city figure for them.
export const LanesCard = ({ vendors = [], onChanged }) => {
  const [lanes, setLanes] = useState([]);
  const [heavyPct, setHeavyPct] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { const r = await listLanes(); setLanes(r.lanes); setHeavyPct(r.heavyPct); } catch (e) { errorToast("Could not load lanes"); } }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { const r = await saveLane(edit); if (r?.success) { setEdit(null); load(); onChanged && onChanged(); } else errorToast(r?.message); }
    catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Remove the ${l.city} lane${l.zohoVendorId ? " override" : ""}?`)) return;
    try { await deleteLane(l.id); load(); onChanged && onChanged(); } catch (e) { errorToast(e?.message); }
  };
  const vendorName = (id) => (vendors.find((v) => v.id === id) || {}).name || id;

  return (
    <div className="card mt-3"><div className="card-body">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div><h6 className="fw-bold mb-0">Transport lanes</h6><div className="small text-muted">Minimum trip cost per vendor city; folded into landed cost per unit. Above {heavyPct ?? "25"}% of goods a requirement is too small to run alone.</div></div>
        <Button size="sm" variant="outline-success" onClick={() => setEdit({ city: "", zohoVendorId: "", minTransport: "", notes: "" })}>Add lane</Button>
      </div>
      <div className="table-responsive"><table className="table table-sm mb-0">
        <thead><tr><th>City</th><th>Applies to</th><th className="text-end">Min transport</th><th>Notes</th><th></th></tr></thead>
        <tbody>
          {lanes.map((l) => (
            <tr key={l.id}>
              <td>{l.city}</td>
              <td className="small">{l.zohoVendorId ? <>only <Link to={`/catalogue/vendors/${l.zohoVendorId}`}>{vendorName(l.zohoVendorId)}</Link></> : <span className="text-muted">every vendor in {l.city}</span>}</td>
              <td className="text-end">{inr(l.minTransport)}</td>
              <td className="small text-muted">{l.notes || ""}</td>
              <td className="text-end"><Button size="sm" variant="link" className="p-0 me-2" onClick={() => setEdit({ id: l.id, city: l.city, zohoVendorId: l.zohoVendorId || "", minTransport: Number(l.minTransport), notes: l.notes || "" })}>edit</Button><Button size="sm" variant="link" className="p-0 text-danger" onClick={() => remove(l)}>remove</Button></td>
            </tr>
          ))}
          {!lanes.length && <tr><td colSpan={5} className="text-muted small">No lanes yet - every requirement prices without transport.</td></tr>}
        </tbody>
      </table></div>
      <Modal show={!!edit} onHide={() => setEdit(null)}>
        <Modal.Header closeButton><Modal.Title>{edit?.id ? "Edit lane" : "New lane"}</Modal.Title></Modal.Header>
        {edit && <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>City (as in the vendor's Zoho record)</Form.Label><Form.Control value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} autoFocus /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Applies to</Form.Label>
            <Form.Select value={edit.zohoVendorId} onChange={(e) => setEdit({ ...edit, zohoVendorId: e.target.value })}>
              <option value="">Every vendor in this city</option>
              {vendors.filter((v) => !v.unidentified).map((v) => <option key={v.id} value={v.id}>Only {v.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2"><Form.Label>Minimum transport (₹ per trip)</Form.Label><Form.Control type="number" min="0" value={edit.minTransport} onChange={(e) => setEdit({ ...edit, minTransport: e.target.value })} /></Form.Group>
          <Form.Group><Form.Label>Notes</Form.Label><Form.Control value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></Form.Group>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy || !edit?.city?.trim() || edit?.minTransport === ""} onClick={save}>Save</Button></Modal.Footer>
      </Modal>
    </div></div>
  );
};

// How a vendor behaves, from the loop's own records over the last 90 days.
const BehaviourCard = ({ vendorId }) => {
  const [s, setS] = useState(null);
  useEffect(() => { vendorStats(vendorId).then(setS).catch(() => setS(null)); }, [vendorId]);
  if (!s) return null;
  const pct = (n) => (n === null || n === undefined ? "—" : `${n}%`);
  return (
    <div className="card mt-3"><div className="card-body">
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="fw-bold mb-0">Behaviour · last {s.windowDays} days</h6>
        <span className={`badge bg-${s.score >= 65 ? "success" : s.score >= 40 ? "warning text-dark" : "danger"}`}>score {s.score}</span>
      </div>
      <div className="small text-muted mb-2">{s.summary}</div>
      <div className="row small g-2">
        <div className="col-6 col-md-3"><div className="text-muted">Response time</div>{s.responseHours === null ? "—" : s.responseHours < 24 ? `${s.responseHours} h` : `${Math.round(s.responseHours / 24)} d`}</div>
        <div className="col-6 col-md-3"><div className="text-muted">Asks answered</div>{pct(s.replyRate)} of {s.requisitionsSent}</div>
        <div className="col-6 col-md-3"><div className="text-muted">Lines completed</div>{pct(s.completeness)} of {s.linesAsked}</div>
        <div className="col-6 col-md-3"><div className="text-muted">Needed clarifying</div>{s.clarificationsNeeded} of {s.repliesInWindow} replies</div>
        <div className="col-6 col-md-3"><div className="text-muted">Rates disputed / invalid</div>{s.ratesDisputed} of {s.ratesLogged}</div>
        <div className="col-6 col-md-3"><div className="text-muted">Last reply</div>{s.lastReply ? new Date(s.lastReply).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</div>
      </div>
      <div className="small text-muted mt-2">Vendors are asked in this order when a requirement needs rates: score first, cheaper lane second.</div>
    </div></div>
  );
};

export const CatalogueVendorList = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", state: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setRows(await listVendors()); } catch (e) { errorToast("Could not load vendors"); } }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try { const r = await createVendor(form); if (r?.success) { successToast(`Created ${r.data.name}`); setShow(false); load(); } else errorToast(r?.message); }
    catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const shown = rows.filter((v) => !q || v.name.toLowerCase().includes(q.toLowerCase()) || (v.phone || "").includes(q));

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-center">
          <div><h6 className="fw-bold mb-0">Vendors</h6><div className="small text-muted">Zoho CRM · {rows.length} vendors</div></div>
          <div className="d-flex gap-2">
            <Form.Control size="sm" placeholder="Name or phone" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
            <Button size="sm" variant="outline-success" onClick={() => setShow(true)}>New vendor</Button>
          </div>
        </div>
        <div className="card mt-2"><div className="card-body p-0">
          <div className="table-responsive"><table className="table table-hover table-sm mb-0">
            <thead><tr><th>Vendor</th><th>Phone</th><th className="text-end">Rates</th><th className="text-end">Products</th><th>Last quote</th></tr></thead>
            <tbody>
              {shown.map((v) => (
                <tr key={v.id} className={v.unidentified ? "text-muted" : ""}>
                  <td><Link to={`/catalogue/vendors/${v.id}`}>{v.name}</Link>{v.unidentified ? <span className="badge bg-light text-dark ms-1">placeholder</span> : null}</td>
                  <td>{v.phone || "—"}</td>
                  <td className="text-end">{v.rateCount}</td>
                  <td className="text-end">{v.productCount}</td>
                  <td>{v.lastQuote || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div></div>
        <LanesCard vendors={rows} />
      </div>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton><Modal.Title>New vendor in Zoho CRM</Modal.Title></Modal.Header>
        <Modal.Body>
          {FIELDS.map(([k, label]) => <Form.Group className="mb-2" key={k}><Form.Label>{label}</Form.Label><Form.Control value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} autoFocus={k === "name"} /></Form.Group>)}
          <div className="small text-muted">The phone is how a WhatsApp reply is matched to this vendor.</div>
        </Modal.Body>
        <Modal.Footer><Button variant="success" disabled={busy || !form.name.trim()} onClick={create}>Create in Zoho</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export const CatalogueVendorPage = () => {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(async () => { try { setV(await getVendor(id)); } catch (e) { errorToast(e?.message || "Could not load"); } }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { const r = await updateVendor(id, edit); if (r?.success) { successToast("Saved to Zoho"); setEdit(null); load(); } else errorToast(r?.message); }
    catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const invalidate = async (r) => {
    if (!window.confirm(`Mark the rate "${r.name}" as Invalid? It stays in the history, but stops being used for pricing.`)) return;
    try { const x = await setRateStatus(r.id, "Invalid"); if (x?.success) load(); else errorToast(x?.message); } catch (e) { errorToast(e?.message); }
  };

  if (!v) return null;
  const current = v.products.filter((g) => g.current);
  const stale = current.filter((g) => (Date.now() - new Date(g.current.effectiveDate).getTime()) / 86400000 > 60).length;

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 mb-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <div className="small"><Link to="/catalogue/vendors">Vendors</Link> / {v.name}</div>
            <h6 className="fw-bold mb-0">{v.name}</h6>
            <div className="small text-muted">{[v.phone, v.email, [v.city, v.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "no contact details"}</div>
            {v.description && <div className="small">{v.description}</div>}
          </div>
          <div className="d-flex gap-2">
            {v.phone && <a className="btn btn-sm btn-outline-success" href={`https://wa.me/${v.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">WhatsApp</a>}
            <Button size="sm" variant="outline-secondary" onClick={() => setEdit({ name: v.name, phone: v.phone || "", email: v.email || "", city: v.city || "", state: v.state || "", description: v.description || "" })}>Edit</Button>
          </div>
        </div>

        <div className="card"><div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold mb-0">Current rates · {current.length} products{stale ? ` · ${stale} older than 60 days` : ""}</h6>
            <Form.Check type="switch" id="all" label="Show full history" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="small" />
          </div>
          <div className="table-responsive"><table className="table table-sm mb-0">
            <thead><tr><th>Variant</th><th className="text-end">Rate</th><th>Quoted on</th><th>Status</th><th>Source</th><th></th></tr></thead>
            <tbody>
              {v.products.map((g) => (showAll ? g.rates : g.current ? [g.current] : []).map((r, i) => (
                <tr key={r.id} className={i > 0 ? "text-muted small" : ""}>
                  <td>{i === 0 ? (g.productId ? <Link to={`/catalogue/products/${g.productId}`}>{g.variantName}</Link> : g.variantName) : ""}</td>
                  <td className="text-end fw-semibold">{inr(r.rate)}</td>
                  <td>{r.effectiveDate}</td>
                  <td>{r.status || <span className="text-muted">—</span>}</td>
                  <td className="small text-muted">{r.source || ""}</td>
                  <td className="text-end">{r.status !== "Invalid" && <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => invalidate(r)}>mark invalid</Button>}</td>
                </tr>
              )))}
              {!v.products.length && <tr><td colSpan={6} className="text-muted">No rates logged for this vendor.</td></tr>}
            </tbody>
          </table></div>
        </div></div>
        <BehaviourCard vendorId={id} />
      </div>

      <Modal show={!!edit} onHide={() => setEdit(null)}>
        <Modal.Header closeButton><Modal.Title>Edit vendor</Modal.Title></Modal.Header>
        {edit && <Modal.Body>
          {[...FIELDS, ["description", "Notes"]].map(([k, label]) => <Form.Group className="mb-2" key={k}><Form.Label>{label}</Form.Label><Form.Control as={k === "description" ? "textarea" : "input"} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} /></Form.Group>)}
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy} onClick={save}>Save to Zoho</Button></Modal.Footer>
      </Modal>
    </div>
  );
};
